// Turns a scan problem (a backend rejection, a failed poll, a client-side
// check) into one normalized object the form can explain in plain words.
//
// The backend sends a machine-readable `code` alongside its English `error`
// (the English stays for the admin Scans tab). Older responses only had the
// English, so those strings are mapped too — the site can deploy before the
// backend does.

// Where a problem is shown: under the address field, under the keywords,
// or as a card where results would appear.
const URL_FIELD_CODES = new Set([
  "not_a_url", "invalid_url", "dns_not_found", "dns_timeout", "private_address",
  "missing_url", "bulk_other_domain",
]);
const KEYWORD_FIELD_CODES = new Set(["missing_keyword", "too_many_keywords"]);

export function problemPlacement(problem) {
  if (!problem) return null;
  if (URL_FIELD_CODES.has(problem.code)) return "url";
  if (KEYWORD_FIELD_CODES.has(problem.code)) return "keyword";
  return "card";
}

const LEGACY_MESSAGES = [
  [/^Could not resolve hostname/, "dns_not_found"],
  [/^Hostname resolution timed out/, "dns_timeout"],
  [/^(Invalid URL|Only http and https URLs)/, "invalid_url"],
  [/^Scanning internal\/private IP/, "private_address"],
  [/^(A URL is required|No valid URL provided|Paste at least one URL)/, "missing_url"],
  [/^(At least one keyword is required|Both URL and at least one keyword)/, "missing_keyword"],
  [/^Maximum \d+ keywords/, "too_many_keywords"],
  [/^Bulk scans can only include URLs from/, "bulk_other_domain"],
  [/robots\.txt/, "robots_blocked"],
  [/^pow_(required|invalid)$/, "security_check"],
  [/^This shared scan( link)? has expired/, "share_expired"],
  [/^Scan cancelled/, "cancelled"],
  [/^Too many requests/, "rate_limited"],
];

// Accepts whatever is in the form's `error` state: "", a legacy string, or a
// problem object. Returns a problem object or null.
export function toProblem(error) {
  if (!error) return null;
  if (typeof error === "object") return error;
  const found = LEGACY_MESSAGES.find(([re]) => re.test(error));
  if (!found) return { code: "other", message: error };
  const problem = { code: found[1], message: error };
  if (found[1] === "too_many_keywords") {
    const m = error.match(/Maximum (\d+)/);
    if (m) problem.limit = Number(m[1]);
  }
  if (found[1] === "bulk_other_domain") {
    const m = error.match(/URLs from (\S+)/);
    if (m) problem.domain = m[1];
  }
  return problem;
}

// A /api/scan (or share) response that wasn't a success.
export function problemFromResponse(status, data, submittedUrl) {
  data = data || {};
  if (status === 429) return { code: "rate_limited" };
  const base = data.code
    ? { code: data.code, message: data.error }
    : toProblem(typeof data.error === "string" ? data.error : "") || { code: "other", message: "" };
  return {
    ...base,
    // What the visitor typed, so a suggested fix keeps their style; the
    // backend's normalized copy (https:// added) is the fallback.
    url: submittedUrl || data.url || null,
    ...(data.limit && { limit: data.limit }),
    ...(data.domain && { domain: data.domain }),
    ...(data.other_urls && { otherUrls: data.other_urls }),
  };
}

export function hostOf(url) {
  if (!url) return "";
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).host.replace(/^www\./i, "");
  } catch {
    return String(url).replace(/^https?:\/\//i, "").split(/[/?#]/)[0].replace(/^www\./i, "");
  }
}

// Client-side check, before anything is sent: catches a pasted sentence or a
// bare word, which the backend can only report as "can't find this site".
export function looksLikeWebAddress(text) {
  const t = (text || "").trim();
  if (!t || /\s/.test(t)) return false;
  const host = t.replace(/^https?:\/\//i, "").split(/[/?#]/)[0].replace(/:\d+$/, "");
  if (host.toLowerCase() === "localhost") return true; // the backend explains this one
  return /^[^.\s]+(\.[^.\s]+)+$/.test(host) && !/[<>"'`]/.test(host);
}

// Endings that are almost always typos. Only suggested after the backend has
// confirmed the typed address doesn't exist.
const ENDING_TYPOS = {
  con: "com", cmo: "com", ocm: "com", comm: "com", cpm: "com", vom: "com", xom: "com", coom: "com",
  inn: "in", orgg: "org", ogr: "org", rog: "org", nett: "net", nte: "net", ner: "net",
};

export function suggestAddress(url) {
  if (!url) return null;
  const hadScheme = /^https?:\/\//i.test(url);
  const withScheme = hadScheme ? url : `https://${url}`;
  let u;
  try { u = new URL(withScheme); } catch { return null; }
  const labels = u.hostname.split(".");
  const last = labels[labels.length - 1].toLowerCase();
  const fix = ENDING_TYPOS[last];
  if (!fix || labels.length < 2) return null;
  labels[labels.length - 1] = fix;
  // co.inn -> co.in, co.ukk -> co.uk
  u.hostname = labels.join(".");
  const shown = u.host.replace(/^www\./i, "");
  const path = `${u.pathname === "/" ? "" : u.pathname}${u.search}`;
  // Keeps the visitor's own style: "wowowl.con" becomes "wowowl.com", not
  // "https://wowowl.com".
  return { url: hadScheme ? `${u.protocol}//${u.host}${path}` : `${u.host}${path}`, label: `${shown}${path}` };
}

const STATUS_NAMES = {
  400: "Bad Request", 401: "Unauthorized", 403: "Forbidden", 404: "Not Found", 405: "Method Not Allowed",
  406: "Not Acceptable", 410: "Gone", 429: "Too Many Requests", 451: "Unavailable For Legal Reasons",
  500: "Internal Server Error", 502: "Bad Gateway", 503: "Service Unavailable", 504: "Gateway Timeout",
  520: "Unknown Error", 521: "Web Server Is Down", 522: "Connection Timed Out", 523: "Origin Is Unreachable",
  524: "A Timeout Occurred", 525: "SSL Handshake Failed", 526: "Invalid SSL Certificate",
};

export function statusLabel(status) {
  return STATUS_NAMES[status] ? `${status} ${STATUS_NAMES[status]}` : String(status);
}

// Why a site couldn't be opened, from the most common failure the scan saw.
export function unreachableReason(status) {
  if (status === 401 || status === 403) {
    return { lead: "The site refused our visit", status, tail: "Some sites block automated visitors." };
  }
  if (status === 404 || status === 410) {
    return { lead: "That page wasn't found", status, tail: "Check the address, or start from the site's home page." };
  }
  if (status === 429) {
    return { lead: "The site is limiting visitors right now", status, tail: "Try again in a few minutes." };
  }
  if (typeof status === "number" && status >= 500) {
    return { lead: "The site is having trouble right now", status, tail: "It may be down for a while. Try again later." };
  }
  if (typeof status === "number") {
    return { lead: "The site didn't return a page", status, tail: "" };
  }
  if (status === "timeout") {
    return { lead: "The site took too long to answer", status: null, tail: "It may be down or very slow." };
  }
  if (status === "redirects") {
    return { lead: "The site kept redirecting us in a loop", status: null, tail: "" };
  }
  return { lead: "We couldn't connect to the site", status: null, tail: "It may be down for a while." };
}

const SCOPE_NAMES = { title: "page titles", headings: "headings", meta: "meta descriptions", body: "body text" };

export function scopeList(scopes) {
  const names = (scopes || []).map(s => SCOPE_NAMES[s] || s);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
